import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Video, VideoOff, Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

// Free Google STUN servers
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

const CallScreen = () => {
  const { matchId, callType } = useParams(); // callType: 'video' or 'audio'
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [callStatus, setCallStatus] = useState('calling'); // calling, ringing, connected, ended
  const [videoEnabled, setVideoEnabled] = useState(callType === 'video');
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const websocketRef = useRef(null);

  useEffect(() => {
    initializeCall();
    
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (websocketRef.current) {
      websocketRef.current.close();
    }
  };

  const initializeCall = async () => {
    try {
      // Load match and user info
      const [meResponse, matchesResponse] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/matches`, { withCredentials: true })
      ]);
      
      setCurrentUser(meResponse.data);
      const match = matchesResponse.data.find(m => m.match_id === matchId);
      
      if (!match) {
        toast.error('Match not found');
        navigate('/matches');
        return;
      }
      
      setOtherUser(match.user);
      
      // Get local media stream
      const constraints = {
        video: callType === 'video' ? { width: 1280, height: 720 } : false,
        audio: true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      // Connect WebSocket
      const wsUrl = `${WS_URL}/ws/call/${meResponse.data.id}`;
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        // Send call request to other user
        const callRequest = {
          type: 'call-request',
          target_user_id: match.user.id,
          match_id: matchId,
          call_type: callType
        };
        console.log('Sending call request:', callRequest);
        ws.send(JSON.stringify(callRequest));
        setCallStatus('ringing');
      };
      
      ws.onmessage = async (event) => {
        console.log('📨 Received WebSocket message:', event.data);
        const message = JSON.parse(event.data);
        await handleSignalingMessage(message, stream);
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        toast.error('Connection error - Could not establish call');
        setTimeout(() => navigate(`/chat/${matchId}`), 2000);
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
      };
      
    } catch (error) {
      console.error('Error initializing call:', error);
      toast.error('Could not access camera/microphone');
      navigate(`/chat/${matchId}`);
    }
  };

  const handleSignalingMessage = async (message, stream) => {
    console.log('📥 Processing signaling message:', message.type);
    
    try {
      switch (message.type) {
        case 'call-accept':
          // Other user accepted, create offer
          console.log('✅ Call accepted by other user');
          await createPeerConnection(stream, message.from_user_id);
          const offer = await peerConnectionRef.current.createOffer();
          await peerConnectionRef.current.setLocalDescription(offer);
          
          websocketRef.current.send(JSON.stringify({
            type: 'offer',
            target_user_id: message.from_user_id,
            data: offer
          }));
          setCallStatus('connecting');
          break;
          
        case 'offer':
          // Received offer, create answer
          console.log('📞 Received offer, creating answer');
          await createPeerConnection(stream, message.from_user_id);
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(message.data));
          const answer = await peerConnectionRef.current.createAnswer();
          await peerConnectionRef.current.setLocalDescription(answer);
          
          websocketRef.current.send(JSON.stringify({
            type: 'answer',
            target_user_id: message.from_user_id,
            data: answer
          }));
          setCallStatus('connecting');
          break;
          
        case 'answer':
          // Received answer
          console.log('✅ Received answer');
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(message.data));
          setCallStatus('connected');
          break;
          
        case 'ice-candidate':
          // Received ICE candidate
          if (message.data && peerConnectionRef.current) {
            console.log('🧊 Adding ICE candidate');
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(message.data));
          }
          break;
          
        case 'call-reject':
          toast.error('Call was rejected');
          endCall();
          break;
          
        case 'call-end':
          toast.info('Call ended by other user');
          endCall();
          break;
          
        default:
          console.log('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Error handling signaling message:', error);
      toast.error('Connection error: ' + error.message);
    }
  };

  const createPeerConnection = async (stream, otherUserId) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionRef.current = pc;
    
    // Add local stream tracks to peer connection
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream);
    });
    
    // Handle remote stream
    pc.ontrack = (event) => {
      console.log('Received remote track');
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
        setCallStatus('connected');
      }
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && websocketRef.current) {
        websocketRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          target_user_id: otherUserId,
          data: event.candidate
        }));
      }
    };
    
    // Handle connection state
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        endCall();
      }
    };
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const endCall = () => {
    // Notify other user if possible
    try {
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && otherUser) {
        websocketRef.current.send(JSON.stringify({
          type: 'call-end',
          target_user_id: otherUser.id
        }));
      }
    } catch (e) {
      console.error('Error sending call-end:', e);
    }
    
    cleanup();
    setCallStatus('ended');
    setTimeout(() => {
      navigate(`/chat/${matchId}`);
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black z-50">
      {/* Remote Video (Full Screen) */}
      <div className="absolute inset-0">
        {callType === 'video' && callStatus === 'connected' ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                {otherUser?.photos?.[0] ? (
                  <img 
                    src={otherUser.photos[0]} 
                    alt={otherUser.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-5xl text-white font-bold">
                    {otherUser?.name?.[0] || '?'}
                  </span>
                )}
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">{otherUser?.name}</h2>
              <p className="text-gray-300 text-lg">
                {callStatus === 'calling' && 'Initiating call...'}
                {callStatus === 'ringing' && 'Ringing...'}
                {callStatus === 'connecting' && 'Connecting...'}
                {callStatus === 'connected' && (callType === 'video' ? 'Connected' : 'Voice Call')}
                {callStatus === 'ended' && 'Call Ended'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Local Video (Picture-in-Picture) */}
      {callType === 'video' && videoEnabled && (
        <div className="absolute top-6 right-6 w-48 h-36 bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/30">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover transform scale-x-[-1]"
          />
        </div>
      )}

      {/* Call Info */}
      <div className="absolute top-6 left-6 text-white">
        <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full">
          <span className="text-sm font-medium">
            {callType === 'video' ? '📹 Video Call' : '📞 Voice Call'}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2">
        <div className="bg-white/10 backdrop-blur-md rounded-full px-8 py-4 flex items-center space-x-6 shadow-2xl">
          {/* Toggle Video */}
          {callType === 'video' && (
            <Button
              onClick={toggleVideo}
              data-testid="toggle-video-btn"
              size="icon"
              className={`w-14 h-14 rounded-full ${
                videoEnabled 
                  ? 'bg-gray-700 hover:bg-gray-600' 
                  : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
            </Button>
          )}

          {/* Toggle Audio */}
          <Button
            onClick={toggleAudio}
            data-testid="toggle-audio-btn"
            size="icon"
            className={`w-14 h-14 rounded-full ${
              audioEnabled 
                ? 'bg-gray-700 hover:bg-gray-600' 
                : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </Button>

          {/* End Call */}
          <Button
            onClick={endCall}
            data-testid="end-call-btn"
            size="icon"
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700"
          >
            <PhoneOff className="w-7 h-7" />
          </Button>
        </div>
      </div>

      {/* Call Duration */}
      {callStatus === 'connected' && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 text-white">
          <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full">
            <span className="text-sm font-medium">00:00</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallScreen;
